import { MockedResponse } from "@apollo/client/testing";
import { waitForOptions } from "@testing-library/react";

import {
  DeviceStatus,
  DeviceTypes,
  EBrilliantControlType,
  ERole,
  EWarningTypes,
  IAlarmNotificationSetting,
  IAlert,
  IBrilliantControlsCard,
  IInstallationDetails,
  IOrganization,
  IProViewHome,
  IRegisteredUser,
  ISmartCardCredentialsData,
  IUnitDetailsDataHome,
  IWarning,
  Role,
} from "../interfaces/interfaces";
import {
  GET_ALERTS_FOR_ORGANIZATION,
  GET_ALERT_NOTIFICATION_SETTINGS,
  GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME,
  GET_HOME,
  GET_INTEGRATION_TYPES_FOR_ORGANIZATION,
  GET_INVITED_RESIDENT_CARD_INFO,
  GET_INVITED_RESIDENT_EMAILS,
  GET_RESIDENT_CARD_INFO,
  GET_RESIDENT_INFO,
  GET_USER,
  GET_USER_ACCOUNT_INFO,
  RESET_INSTALLATION,
  UPDATE_CARD_CREDENTIALS,
} from "../api/gqlQueries";

import { ENTRATA, REAL_PAGE, REMOTE_LOCK } from "./integrationUtils";
import {
  NO_RECONFIGURATION,
  RECONFIGURATION_STARTED,
} from "./reconfigurationUtils";
import { AlertTypes } from "./alertUtils";
import { SMART_CARD_LOCK_TYPE } from "./utils";

export const MOCK_WAIT_FOR_ELEMENT_OPTIONS: waitForOptions = {
  timeout: 3000,
};

export const MOCK_EMAIL_ADDRESS = "fred@brilliant.tech";
export const MOCK_USER_ID = "01705f3827f10001a16ac9d1ef6afa9d";

/**
 * Home Mocks
 */

export const emptyHomeMock = ({
  id = "home_property_id",
  isInstallIncomplete = false,
  propertyName = "101",
  reconfigurationState = NO_RECONFIGURATION,
}: {
  id?: string;
  isInstallIncomplete?: boolean;
  propertyName?: string;
  reconfigurationState?: string;
}): IUnitDetailsDataHome => {
  return {
    __typename: "Home",
    alerts: [],
    brilliantControls: [],
    brilliantPlugs: [],
    brilliantSwitches: [],
    floor: 1,
    homeId: "H01705f3827f10001a16ac9d1ef6afa9",
    homePasscode: "1234",
    id,
    installationDetails: {
      installedBrilliantControls: [],
      installedBrilliantPlugs: [],
      installedBrilliantSwitches: [],
      installedIntegrationDevices: [],
    },
    integrations: [],
    isHubless: false,
    isInstallIncomplete,
    numBrilliantControls: 0,
    numBrilliantPlugs: 0,
    numBrilliantSwitches: 0,
    numIntegrations: 0,
    numInvitedResidents: 0,
    numOfflineDevices: 0,
    numRemotelockDevices: 0,
    numResidents: 0,
    parentProperties: [],
    propertyName,
    reconfigurationState,
    scheduledConfigurations: [],
    warnings: [],
    wifiCredentials: {
      networkName: "",
      networkType: "Visible",
      password: "",
      securityType: "wpa/wpa2",
    },
  };
};

export const getEmptyHomeInReconfiguration = emptyHomeMock({
  reconfigurationState: RECONFIGURATION_STARTED,
});

export const GetEmptyHomeRequestMock = ({
  id = "home_property_id",
  isInstallIncomplete = false,
  propertyName = "101",
}: {
  id?: string;
  isInstallIncomplete?: boolean;
  propertyName?: string;
}): MockedResponse => {
  return {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: id,
      },
    },
    result: {
      data: {
        home: emptyHomeMock({
          id,
          isInstallIncomplete,
          propertyName,
        }),
      },
    },
  };
};

/**
 * Warning Mocks
 */

export const InstallIncompleteWarning = ({
  warningType = EWarningTypes.InstallIncomplete,
}: {
  id?: string;
  warningType?: EWarningTypes;
}): IWarning => {
  return {
    warningType,
  };
};

export const DevicesOfflineWarning = ({
  warningType = EWarningTypes.OfflineDevices,
}: {
  id?: string;
  warningType?: EWarningTypes;
}): IWarning => {
  return {
    warningType,
  };
};

/**
 * Climate Alarm Mocks
 */

export const leakDetectedClimateAlarm = ({
  id = "leak_alert_1",
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "My device",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "LeakAlert",
    alarmType: AlertTypes.LeakDetected,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const extremeTemperatureClimateAlarm = ({
  id = "extreme_temperature_alert_1",
  ambientTemperatureF = 120,
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "My device",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  ambientTemperatureF?: number;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "ExtremeTemperatureClimateAlarm",
    alarmType: AlertTypes.ExtremeTemperature,
    ambientTemperatureF,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const lowBatteryAlert = ({
  id = "extreme_temperature_alert_1",
  deviceType = "leak detector",
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "My device",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  deviceType?: string;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "LowBatteryAlert",
    alarmType: AlertTypes.LowBattery,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    deviceType,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const devicesOfflineAlert = ({
  id = "offline_devices_1",
  deviceType = "control",
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "My device",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  deviceType?: string;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "OfflineDevicesAlert",
    alarmType: AlertTypes.OfflineDevices,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    deviceType,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const invitationFailedAlertMock = ({
  id = "invitation_failed_alert_1",
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "InvitationFailedAlert",
    alarmType: AlertTypes.ResidentInviteFailed,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const lowBatteryAlertMock = ({
  id = "invitation_failed_alert_1",
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "LowBatteryAlert",
    alarmType: AlertTypes.LowBattery,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const reconfigurationTakingTooLongAlertMock = ({
  id = "reconfiguration_taking_too_long_alert_1",
  buildingId = "01705f3827f10001a16ac9d1ef6afa9b",
  buildingName = "Sunny Days",
  deviceName = "",
  homePropertyId = "H01705f3827f10001a16ac9d1ef6afa9h",
  homePropertyName = "101",
  regionId = "",
  regionName = "",
}: {
  id?: string;
  buildingId?: string;
  buildingName?: string;
  deviceName?: string;
  homePropertyId?: string;
  homePropertyName?: string;
  regionId?: string;
  regionName?: string;
}): IAlert => {
  return {
    __typename: "ReconfigurationTakingTooLong",
    alarmType: AlertTypes.ReconfigurationTakingTooLong,
    building: {
      __typename: "Building",
      id: buildingId,
      propertyName: buildingName,
    },
    deviceName,
    home: emptyHomeMock({ id: homePropertyId, propertyName: homePropertyName }),
    id,
    region: {
      __typename: "Region",
      id: regionId,
      propertyName: regionName,
    },
  };
};

export const organizationId = "01707948c4690009ac79bbf8dceb2b37";
export const propertyId = "0177d5a8e54d00092c942cf05e450d30";

export const singleBrilliantControl = ({
  connectivityStatus = DeviceStatus.Online,
  id = "0171a7fac0b800038bdbec39b55dae9a",
  isReconfiguring = false,
  lastKnownCloudSync = "",
  name = "Bedroom",
  numGangs = 1,
  roomName = "Bedroom",
  controlType = EBrilliantControlType.REGULAR,
}: {
  id?: string;
  isReconfiguring?: boolean;
  name?: string;
  numGangs?: number;
  connectivityStatus?: DeviceStatus;
  lastKnownCloudSync?: string;
  roomName?: string;
  controlType?: EBrilliantControlType;
}): IBrilliantControlsCard => {
  return {
    connectivityStatus,
    controlType,
    id,
    isReconfiguring,
    lastKnownCloudSync,
    name,
    numGangs,
    roomName,
  };
};

export const EmptyIntegrationTypesForOrganization = {
  request: {
    query: GET_INTEGRATION_TYPES_FOR_ORGANIZATION,
    variables: {
      organizationId: "",
    },
  },
  result: {
    data: {
      organization: {
        integrations: [
          {
            integrationType: REMOTE_LOCK,
          },
        ],
      },
    },
  },
};

export const EntrataIntegrationTypesForOrganization = {
  request: {
    query: GET_INTEGRATION_TYPES_FOR_ORGANIZATION,
    variables: {
      organizationId: "",
    },
  },
  result: {
    data: {
      organization: {
        integrations: [
          {
            integrationType: ENTRATA,
          },
        ],
      },
    },
  },
};

const RealPageIntegrationTypesForOrganization = {
  request: {
    query: GET_INTEGRATION_TYPES_FOR_ORGANIZATION,
    variables: {
      organizationId: "",
    },
  },
  result: {
    data: {
      organization: {
        integrations: [
          {
            integrationType: REAL_PAGE,
          },
        ],
      },
    },
  },
};

export const GetAlertsForOrganizationWithLeakDetectedAlertMock = {
  request: {
    query: GET_ALERTS_FOR_ORGANIZATION,
    variables: {
      organizationId: "",
    },
  },
  result: {
    data: {
      organization: {
        alerts: [leakDetectedClimateAlarm({})],
        id: "",
      },
    },
  },
};

export const GetAlertsForOrganizationWithNoAlertsMock = {
  request: {
    query: GET_ALERTS_FOR_ORGANIZATION,
    variables: {
      organizationId: "",
    },
  },
  result: {
    data: {
      organization: {
        alerts: [],
        id: "",
      },
    },
  },
};

export const GetUserMock = ({
  familyName = "Mercury",
  givenName = "Freddie",
  userId = MOCK_USER_ID,
  id = "11705f3827f10001a16ac9d1ef6afa9d",
  birthdate = "04/19/1990",
  role = ERole.MDU_OPERATOR,
  organizations = [],
}: {
  familyName?: string;
  givenName?: string;
  userId?: string;
  id?: string;
  birthdate?: string;
  role?: Role;
  organizations?: IOrganization[];
}): MockedResponse => {
  return {
    request: {
      query: GET_USER,
      variables: {
        userId,
      },
    },
    result: {
      data: {
        user: {
          birthdate,
          familyName,
          givenName,
          id,
          organizations,
          role,
        },
      },
    },
  };
};

export const GetUserWithAccountInfoMock = ({
  familyName = "Mercury",
  givenName = "Freddie",
  userId = MOCK_USER_ID,
  id = "11705f3827f10001a16ac9d1ef6afa9d",
  birthdate = "04/19/1990",
  mfaPhoneNumber = "+13334445555",
}: {
  familyName?: string;
  givenName?: string;
  userId?: string;
  id?: string;
  birthdate?: string;
  mfaPhoneNumber?: string;
}): MockedResponse => {
  return {
    request: {
      query: GET_USER_ACCOUNT_INFO,
      variables: {
        userId,
      },
    },
    result: {
      data: {
        user: {
          birthdate,
          familyName,
          givenName,
          id,
          mfaPhoneNumber,
        },
      },
    },
  };
};

export const GetAlertNotificationSettingsMock = ({
  id = "11705f3827f10001a16ac9d1ef6afa9d",
  alarmNotificationSettings = [],
  buildingId = "01705f3827f10001a16ac9d1ef6rfa9a",
  userId = MOCK_USER_ID,
}: {
  userId?: string;
  id?: string;
  buildingId?: string;
  alarmNotificationSettings?: IAlarmNotificationSetting[];
}): MockedResponse => {
  return {
    request: {
      query: GET_ALERT_NOTIFICATION_SETTINGS,
      variables: {
        propertyId: buildingId,
        userId,
      },
    },
    result: {
      data: {
        user: {
          alarmNotificationSettings,
          id,
        },
      },
    },
  };
};

export const GetLeakDetectedAlertMocks = [
  GetAlertsForOrganizationWithLeakDetectedAlertMock,
];

export const GetLeakDetectedWithMultipleAlertMocksForProView = [
  {
    request: {
      query: GET_ALERTS_FOR_ORGANIZATION,
      variables: {
        organizationId: "",
      },
    },
    result: {
      data: {
        organization: {
          alerts: [
            leakDetectedClimateAlarm({
              buildingId: "",
              buildingName: "",
              regionId: "01705f3827f10001a16ac9d1ef6afa9b",
              regionName: "Sunny Days",
            }),
            devicesOfflineAlert({
              buildingId: "",
              buildingName: "",
              regionId: "01705f3827f10001a16ac9d1ef6afa9b",
              regionName: "Sunny Days",
            }),
            lowBatteryAlertMock({
              buildingId: "",
              buildingName: "",
              regionId: "01705f3827f10001a16ac9d1ef6afa9b",
              regionName: "Sunny Days",
            }),
          ],
          id: organizationId,
        },
      },
    },
  },
];

export const GetLeakDetectedWithMultipleAlertMocks = [
  {
    request: {
      query: GET_ALERTS_FOR_ORGANIZATION,
      variables: {
        organizationId: "",
      },
    },
    result: {
      data: {
        organization: {
          alerts: [
            leakDetectedClimateAlarm({}),
            extremeTemperatureClimateAlarm({
              homePropertyId: "H01705f3827f10001a16ac9d1ef6afa9je",
              homePropertyName: "101",
            }),
            devicesOfflineAlert({}),
            extremeTemperatureClimateAlarm({
              homePropertyId: "H01705f3827f10001a16ac9d1ef6afa9jt",
              homePropertyName: "102",
              id: "extreme_temperature_alert_2",
            }),
            leakDetectedClimateAlarm({
              buildingId: "01705f3827f10001a16ac9d1ef6afa9a",
              buildingName: "Smart Building",
              homePropertyId: "H01705f3827f10001a16ac9d1ef6afa9j",
              homePropertyName: "102",
              id: "opaque_climate_alarm_id_0",
            }),
            leakDetectedClimateAlarm({
              buildingId: "01705f3827f10001a16ac9d1ef6afa9a",
              buildingName: "Smart Building",
              homePropertyId: "H01705f3827f10001a16ac9d1ef6afa9k",
              id: "opaque_climate_alarm_id_1",
            }),
            invitationFailedAlertMock({}),
            reconfigurationTakingTooLongAlertMock({}),
            lowBatteryAlertMock({}),
          ],
          id: organizationId,
        },
      },
    },
  },
];

export const homePropertyName = "1300 22nd #114";
export const homeName = "Ashley's Home";
export const buildingName = "Four50 Residences";
export const homePropertyId = "0171a7idc0b800038bdbec39b55dae9d";
export const homeId = "0171a7home0800038bdbec39b55dae9d";

export const invitedTenant = {
  emailAddress: "roger@brilliant.tech",
  expirationDatetime: "2033-06-20T20:12:00",
  id: "0170eef7b6a400018f32b810a6e47ced",
  remotelockCardNumber: null,
};

export const invitedTenantEmails = [
  {
    emailAddress: "roger@brilliant.tech",
  },
  {
    emailAddress: "roger+1@brilliant.tech",
  },
  {
    emailAddress: "roger+2@brilliant.tech",
  },
];

export const onlyInvitedTenantEmailsMocks = [
  {
    request: {
      query: GET_INVITED_RESIDENT_EMAILS,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          invitedResidents: invitedTenantEmails,
        },
      },
    },
  },
];

export const getTenant = ({
  deletedDatetime = null,
  emailAddress = "freddie@brilliant.tech",
  familyName = "Mercury",
  givenName = "Freddie",
  isAdmin = false,
  id = "0170794953940001ce2a630c662cc7d5",
  joinedPropertyDatetime = "2020-06-20T20:12:00",
  remotelockCardNumber = null,
}: {
  deletedDatetime?: string | null;
  emailAddress?: string;
  familyName?: string;
  givenName?: string;
  isAdmin?: boolean;
  id?: string;
  joinedPropertyDatetime?: string;
  remotelockCardNumber?: string | null;
}): IRegisteredUser => {
  return {
    deletedDatetime,
    emailAddress,
    familyName,
    givenName,
    id,
    isAdmin,
    joinedPropertyDatetime,
    remotelockCardNumber,
  };
};

const getTooManyTenants = () => {
  let tenants: IRegisteredUser[] = [];
  for (let i = 0; i < 25; i += 1) {
    tenants = [...tenants, getTenant({})];
  }
  return tenants;
};

export const residentInfoWithMultipleTenants = {
  request: {
    query: GET_RESIDENT_INFO,
    variables: {
      propertyId: homePropertyId,
    },
  },
  result: {
    data: {
      home: {
        invitedResidents: [],
        residents: [getTenant({}), getTenant({})],
      },
    },
  },
};

export const residentInfoWithTooManyTenants = {
  request: {
    query: GET_RESIDENT_INFO,
    variables: {
      propertyId: homePropertyId,
    },
  },
  result: {
    data: {
      home: {
        invitedResidents: [],
        residents: getTooManyTenants(),
      },
    },
  },
};

export const residentInfoWithTenantAndInvitedTenant = {
  request: {
    query: GET_RESIDENT_INFO,
    variables: {
      propertyId: homePropertyId,
    },
  },
  result: {
    data: {
      home: {
        invitedResidents: [invitedTenant],
        residents: [getTenant({})],
      },
    },
  },
};

export const residentInfoWithNoTenants = {
  request: {
    query: GET_RESIDENT_INFO,
    variables: {
      propertyId: homePropertyId,
    },
  },
  result: {
    data: {
      home: {
        invitedResidents: [],
        residents: [],
      },
    },
  },
};

export const multipleTenantMocks = [residentInfoWithMultipleTenants];

export const apolloMocksWithNoAlerts = [
  GetAlertsForOrganizationWithNoAlertsMock,
];

export const apolloMocksWithExtremeTemperatureMock = [
  {
    request: {
      query: GET_ALERTS_FOR_ORGANIZATION,
      variables: {
        organizationId: "",
      },
    },
    result: {
      data: {
        organization: {
          __typename: "Organization",
          alerts: [extremeTemperatureClimateAlarm({})],
          id: "",
        },
      },
    },
  },
];

export const wifiCredentials = {
  networkName: "NETGEAR-1",
  networkType: "Visible",
  password: "BrilliantPassword",
  securityType: "wpa/wpa2",
};

export const emptyWifiCredentials = {
  networkName: "",
  networkType: "Visible",
  password: "",
  securityType: "wpa/wpa2",
};

export const getHomeSingleExtremeTemperatureClimateAlarmMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [extremeTemperatureClimateAlarm({})],
          brilliantControls: [],
          brilliantPlugs: [],
          brilliantSwitches: [],

          homeId: "123456",
          id: "12",
          integrations: [],
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties: [
            {
              name: "Four50 Residences",
              propertyId: "123",
            },
            {
              name: "CA Ventures",
              propertyId: "1234",
            },
          ],
          propertyName: "1300 22nd #114",
          reconfigurationState: NO_RECONFIGURATION,
          scheduledConfigurations: [],
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const getHomeSingleResidentInvitationFailedAlertMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [invitationFailedAlertMock({})],
          brilliantControls: [],
          brilliantPlugs: [],
          brilliantSwitches: [],

          homeId: "123456",
          id: "12",
          integrations: [],
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties: [
            {
              name: "Four50 Residences",
              propertyId: "123",
            },
            {
              name: "CA Ventures",
              propertyId: "1234",
            },
          ],
          propertyName: "1300 22nd #114",
          reconfigurationState: NO_RECONFIGURATION,
          scheduledConfigurations: [],
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const brilliantSwitches = [
  {
    connectivityStatus: DeviceStatus.Online,
    id: "0171a7fac0b800078bdbec39b55dae9d",
    isReconfiguring: false,
    name: "Bedroom Switch",
  },
];
export const brilliantPlugs = [
  {
    connectivityStatus: DeviceStatus.Online,
    id: "0171a7fac0b800088bdbec39b55dae9g",
    isReconfiguring: false,
    name: "Living Room Plug111111111111111",
  },
];
export const installedBrilliantPlugs = [
  {
    connectivityStatus: DeviceStatus.Online,
    id: "0171a7fac0b800088bdbec39b55dae9g",
    isReconfiguring: false,
    name: "Living Room Plug111111111111111",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    id: "0171a7fac0b800088bdbec39b55dae9h",
    isReconfiguring: false,
    name: "Living Room Plug2",
  },
];

export const parentProperties = [
  {
    name: buildingName,
    propertyId: "123",
  },
  {
    name: "CA Ventures",
    propertyId: "1234",
  },
];

export const integrations = [
  {
    integrationDevices: [
      {
        connectivityStatus: DeviceStatus.Online,
        deviceType: DeviceTypes.UNKNOWN,
        name: "august Device",
      },
    ],
    name: "august",
    numDevices: 4,
  },
  {
    integrationDevices: [
      {
        connectivityStatus: DeviceStatus.Online,
        deviceType: DeviceTypes.UNKNOWN,
        name: "ecobee Device",
      },
    ],
    name: "ecobee",
    numDevices: 3,
  },
];

export const brilliantControlsReconfiguring = [
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9b",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Living room",
    numGangs: 2,
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9c",
    isReconfiguring: true,
    lastKnownCloudSync: "",
    name: "Bathroom",
    numGangs: 3,
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9d",
    isReconfiguring: true,
    lastKnownCloudSync: "",
    name: "Kitchen",
    numGangs: 4,
    roomName: "Kitchen",
  },
];

export const brilliantControls = [
  singleBrilliantControl({}),
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9b",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Living room",
    numGangs: 2,
    roomName: "Living room",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9c",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Bathroom",
    numGangs: 3,
    roomName: "Bathroom",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9d",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Kitchen",
    numGangs: 4,
    roomName: "Kitchen",
  },
];

export const installedBrilliantControls = [
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9b",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Living room",
    numGangs: 2,
    roomName: "Living room",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9c",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Bathroom",
    numGangs: 3,
    roomName: "Bathroom",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9d",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Kitchen",
    numGangs: 4,
    roomName: "Kitchen",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9e",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Kitchen 2",
    numGangs: 2,
    roomName: "Kitchen",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9f",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Kitchen 3",
    numGangs: 2,
    roomName: "Kitchen",
  },
];

export const installedIntegrations = [
  {
    id: "testID1",
    integrationType: "Honeywell Home",
    name: "Honeywell Thermostat",
  },
  {
    id: "testID2",
    integrationType: "Honeywell Home",
    name: "Honeywell Heater",
  },
  {
    id: "testID3",
    integrationType: "Ecobee",
    name: "Ecobee 1",
  },
  {
    id: "testID4",
    integrationType: "Ecobee",
    name: "Ecobee 2",
  },
  {
    id: "testID5",
    integrationType: "Ring",
    name: "Ring Device",
  },
];

export const computedBrilliantControlList = [
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9c",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Bathroom",
    numGangs: 3,
    roomName: "Bathroom",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9a",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Bedroom",
    numGangs: 1,
    roomName: "Bedroom",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9d",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Kitchen",
    numGangs: 4,
    roomName: "Kitchen",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9e",
    isReconfiguring: true,
    lastKnownCloudSync: "",
    name: "Kitchen 2",
    numGangs: 2,
    roomName: "Kitchen",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9f",
    isReconfiguring: true,
    lastKnownCloudSync: "",
    name: "Kitchen 3",
    numGangs: 2,
    roomName: "Kitchen",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    controlType: EBrilliantControlType.REGULAR,
    id: "0171a7fac0b800038bdbec39b55dae9b",
    isReconfiguring: false,
    lastKnownCloudSync: "",
    name: "Living room",
    numGangs: 2,
    roomName: "Living room",
  },
];

export const computedBrilliantPlugsList = [
  {
    connectivityStatus: DeviceStatus.Online,
    id: "0171a7fac0b800088bdbec39b55dae9g",
    isReconfiguring: false,
    name: "Living Room Plug111111111111111",
  },
  {
    connectivityStatus: DeviceStatus.Online,
    id: "0171a7fac0b800088bdbec39b55dae9h",
    isReconfiguring: true,
    name: "Living Room Plug2",
  },
];

export const scheduledConfiguration = {
  configurationTitle: "Vacant",
  createdDatetime: "2020-06-20T20:12:00",
  id: "0172b9f4bebe000a830e8e4caef0ffcd",
  scheduledDatetime: "2020-06-30T20:12:00",
};

export const scheduledConfigurations = [scheduledConfiguration];

export const getOccupiedHome = ({
  alerts = [],
  id = homePropertyId,
  isInstallIncomplete = false,
  numResidents = 3,
  installationDetails = {
    installedBrilliantControls: [],
    installedBrilliantPlugs: [],
    installedBrilliantSwitches: [],
    installedIntegrationDevices: [],
  },
  reconfigurationState = NO_RECONFIGURATION,
  warnings = [],
}: {
  alerts?: IAlert[];
  id?: string;
  isInstallIncomplete?: boolean;
  numResidents?: number;
  installationDetails?: IInstallationDetails;
  reconfigurationState?: string;
  warnings?: IWarning[];
}): IProViewHome => {
  return {
    __typename: "Home",
    alerts,
    brilliantControls,
    brilliantPlugs,
    brilliantSwitches,
    floor: 1,
    homeId,
    homeName,
    homePasscode: "1234",
    id,
    installationDetails,
    integrations,
    isHubless: false,
    isInstallIncomplete,
    numBrilliantControls: 4,
    numBrilliantPlugs: 1,
    numBrilliantSwitches: 1,
    numIntegrations: 1,
    numInvitedResidents: 0,
    numOfflineDevices: 0,
    numRemotelockDevices: 5,
    numResidents,
    parentProperties,
    propertyName: homePropertyName,
    reconfigurationState,
    scheduledConfigurations,
    warnings,
    wifiCredentials,
  };
};
export const occupiedHome = getOccupiedHome({});

export const installationDetails = {
  installedBrilliantControls,
  installedBrilliantPlugs,
  installedBrilliantSwitches: brilliantSwitches,
  installedIntegrationDevices: installedIntegrations,
};

export const brilliantDevices = {
  installedBrilliantControls: brilliantControls,
  installedBrilliantPlugs: brilliantPlugs,
  installedBrilliantSwitches: brilliantSwitches,
};

export const brilliantDevicesReconfiguring = {
  installedBrilliantControls: brilliantControlsReconfiguring,
  installedBrilliantPlugs: brilliantPlugs,
  installedBrilliantSwitches: brilliantSwitches,
};

export const getOccupiedHomeWithInstallationDetails = getOccupiedHome({
  installationDetails,
  reconfigurationState: RECONFIGURATION_STARTED,
});

export const getOccupiedHomeWithInstallationDetailsAndReconfigurationAlert =
  getOccupiedHome({
    alerts: [reconfigurationTakingTooLongAlertMock({})],
    installationDetails,
    reconfigurationState: RECONFIGURATION_STARTED,
  });

export const vacantHome = {
  __typename: "Home",
  alerts: [],
  brilliantControls: [
    singleBrilliantControl({}),
    singleBrilliantControl({ numGangs: 0 }),
  ],
  brilliantPlugs,
  brilliantSwitches,
  floor: 1,
  homeId,
  homePasscode: "1234",
  id: homePropertyId,
  installationDetails: {
    installedBrilliantControls: [],
    installedBrilliantPlugs: [],
    installedBrilliantSwitches: [],
    installedIntegrationDevices: [],
  },
  integrations,
  isHubless: false,
  isInstallIncomplete: false,
  numBrilliantControls: 4,
  numBrilliantPlugs: 1,
  numBrilliantSwitches: 1,
  numIntegrations: 1,
  numInvitedResidents: 0,
  numOfflineDevices: 0,
  numRemotelockDevices: 0,
  numResidents: 0,
  parentProperties,
  propertyName: homePropertyName,
  reconfigurationState: NO_RECONFIGURATION,
  scheduledConfigurations,
  warnings: [],
  wifiCredentials,
};

export const vacantHomeInReconfiguration = {
  __typename: "Home",
  alerts: [],
  brilliantControls: [singleBrilliantControl({})],
  brilliantPlugs,
  brilliantSwitches,
  floor: 1,
  homeId,
  id: homePropertyId,
  installationDetails: {
    installedBrilliantControls: [singleBrilliantControl({})],
    installedBrilliantPlugs,
    installedBrilliantSwitches: brilliantSwitches,
    installedIntegrationDevices: installedIntegrations,
  },
  integrations,
  isInstallIncomplete: false,
  numBrilliantControls: 4,
  numBrilliantPlugs: 1,
  numBrilliantSwitches: 1,
  numIntegrations: 1,
  numInvitedResidents: 0,
  numRemotelockDevices: 0,
  numResidents: 0,
  parentProperties,
  propertyName: homePropertyName,
  reconfigurationState: RECONFIGURATION_STARTED,
  scheduledConfigurations,
  warnings: [],
  wifiCredentials,
};

export const singleClimateAlarmWhileReconfigurationMock = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [
            leakDetectedClimateAlarm({
              buildingName,
              deviceName: "Kitchen Leak Detector",
              homePropertyName,
              id: "01705f3827f10001a16ac9d1ef6afa9q",
            }),
          ],
          brilliantControls,
          brilliantPlugs,
          brilliantSwitches,
          homeId,
          id: homePropertyId,
          installationDetails: {
            installedBrilliantControls: brilliantControls,
            installedBrilliantPlugs,
            installedBrilliantSwitches: brilliantSwitches,
            installedIntegrationDevices: installedIntegrations,
          },
          integrations,
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties,
          propertyName: homePropertyName,
          reconfigurationState: RECONFIGURATION_STARTED,
          scheduledConfigurations,
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const getHomeSingleLowBatteryAlertMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [lowBatteryAlert({})],
          brilliantControls: [],
          brilliantPlugs: [],
          brilliantSwitches: [],

          homeId: "123456",
          id: "12",
          integrations: [],
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties: [
            {
              name: "Four50 Residences",
              propertyId: "123",
            },
            {
              name: "CA Ventures",
              propertyId: "1234",
            },
          ],
          propertyName: "1300 22nd #114",
          reconfigurationState: NO_RECONFIGURATION,
          scheduledConfigurations: [],
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const getHomeSingleDevicesOfflineAlertMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [devicesOfflineAlert({})],
          brilliantControls: [],
          brilliantPlugs: [],
          brilliantSwitches: [],

          homeId: "123456",
          id: "12",
          integrations: [],
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties: [
            {
              name: "Four50 Residences",
              propertyId: "123",
            },
            {
              name: "CA Ventures",
              propertyId: "1234",
            },
          ],
          propertyName: "1300 22nd #114",
          reconfigurationState: NO_RECONFIGURATION,
          scheduledConfigurations: [],
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const vacantHomeWithWarning = {
  __typename: "Home",
  alerts: [],
  brilliantControls: [singleBrilliantControl({})],
  brilliantPlugs,
  brilliantSwitches,
  floor: 1,
  homeId,
  id: homePropertyId,
  integrations,
  isInstallIncomplete: false,
  numBrilliantControls: 4,
  numBrilliantPlugs: 1,
  numBrilliantSwitches: 1,
  numIntegrations: 1,
  numInvitedResidents: 0,
  numRemotelockDevices: 0,
  numResidents: 0,
  parentProperties,
  propertyName: homePropertyName,
  reconfigurationState: NO_RECONFIGURATION,
  scheduledConfigurations,
  warnings: [InstallIncompleteWarning({})],
  wifiCredentials,
};

export const emptyVacantHome = {
  __typename: "Home",
  alerts: [],
  brilliantControls: [],
  brilliantPlugs: [],
  brilliantSwitches: [],
  floor: 1,
  homeId,
  id: homePropertyId,
  integrations: [],
  isInstallIncomplete: false,
  numBrilliantControls: 0,
  numBrilliantPlugs: 0,
  numBrilliantSwitches: 0,
  numIntegrations: 0,
  numInvitedResidents: 0,
  numRemotelockDevices: 0,
  numResidents: 0,
  parentProperties,
  propertyName: homePropertyName,
  reconfigurationState: NO_RECONFIGURATION,
  scheduledConfigurations,
  warnings: [],
  wifiCredentials: emptyWifiCredentials,
};

export const GetOccupiedHomeRequestMock = ({
  id = homePropertyId,
  isInstallIncomplete = false,
  numResidents = 3,
}: {
  id?: string;
  isInstallIncomplete?: boolean;
  numResidents?: number;
}): MockedResponse => {
  return {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: id,
      },
    },
    result: {
      data: {
        home: getOccupiedHome({ id, isInstallIncomplete, numResidents }),
      },
    },
  };
};

export const occupiedMocks = [
  GetOccupiedHomeRequestMock({}),
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const occupiedMocksWithEntrataIntegration = [
  GetOccupiedHomeRequestMock({}),
  EntrataIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const occupiedMocksWithRealPageIntegration = [
  GetOccupiedHomeRequestMock({}),
  RealPageIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const vacantMocksWithEntrataIntegration = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: vacantHome,
      },
    },
  },
  EntrataIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const vacantMocksWithRealPageIntegration = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: vacantHome,
      },
    },
  },
  RealPageIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const vacantMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: vacantHome,
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const vacantMocksInReconfigurationState = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: vacantHomeInReconfiguration,
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const vacantMocksWithReconfiguringDevices = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: getOccupiedHomeWithInstallationDetails,
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const occupiedMocksWithReconfiguringTakingTooLongAlert = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: getOccupiedHomeWithInstallationDetailsAndReconfigurationAlert,
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const vacantMocksWithWarning = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: vacantHomeWithWarning,
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const emptyVacantMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: emptyVacantHome,
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithNoTenants,
];

export const singleClimateAlarmMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [
            leakDetectedClimateAlarm({
              buildingName,
              deviceName: "Kitchen Leak Detector",
              homePropertyName,
              id: "01705f3827f10001a16ac9d1ef6afa9q",
            }),
          ],
          brilliantControls,
          brilliantPlugs,
          brilliantSwitches,
          homeId,
          id: homePropertyId,
          integrations,
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties,
          propertyName: homePropertyName,
          reconfigurationState: NO_RECONFIGURATION,
          scheduledConfigurations,
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const multipleAlertsMocks = [
  {
    request: {
      query: GET_HOME,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          __typename: "Home",
          alerts: [
            leakDetectedClimateAlarm({
              buildingName,
              deviceName: "Kitchen Leak Detector",
              homePropertyName,
              id: "01705f3827f10001a16ac9d1ef6afa9q",
            }),
            leakDetectedClimateAlarm({
              buildingName,
              deviceName: "Living Room Leak Detector",
              homePropertyName,
              id: "0171a7fac0b800038bdbec39b55dae9m",
            }),
            extremeTemperatureClimateAlarm({}),
          ],
          brilliantControls,
          brilliantPlugs,
          brilliantSwitches,
          homeId,
          id: homePropertyId,
          integrations,
          numInvitedResidents: 0,
          numRemotelockDevices: 0,
          numResidents: 3,
          parentProperties,
          propertyName: homePropertyName,
          reconfigurationState: NO_RECONFIGURATION,
          scheduledConfigurations,
          warnings: [],
          wifiCredentials,
        },
      },
    },
  },
  EmptyIntegrationTypesForOrganization,
  residentInfoWithMultipleTenants,
];

export const onlyInvitedTenantMocks = [
  {
    request: {
      query: GET_RESIDENT_INFO,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          invitedResidents: [invitedTenant],
          residents: [],
        },
      },
    },
  },
];

export const expiredTenantInvite = {
  emailAddress: "rogerExpired@brilliant.tech",
  expirationDatetime: "2020-06-20T20:12:00",
  id: "0170eef7b6a400018f32b810a6e47ced",
  remotelockCardNumber: null,
};

export const expiredTenantInvite2 = {
  emailAddress: "rogerExpired2@brilliant.tech",
  expirationDatetime: "2019-06-20T20:12:00",
  id: "0170eef7b6a400018f32b810a6e47cef",
  remotelockCardNumber: null,
};

export const onlyExpiredInvitedTenantMocks = [
  {
    request: {
      query: GET_RESIDENT_INFO,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          invitedResidents: [expiredTenantInvite],
          residents: [],
        },
      },
    },
  },
];

export const expiredAndInvitedTenantMocks = [
  {
    request: {
      query: GET_RESIDENT_INFO,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          invitedResidents: [
            expiredTenantInvite,
            expiredTenantInvite2,
            invitedTenant,
          ],
          residents: [getTenant({})],
        },
      },
    },
  },
];

export const onlyTenantRequestMock = {
  request: {
    query: GET_RESIDENT_INFO,
    variables: {
      propertyId: homePropertyId,
    },
  },
  result: {
    data: {
      home: {
        invitedResidents: [],
        residents: [getTenant({})],
      },
    },
  },
};

export const onlyAdminRequestMock = {
  request: {
    query: GET_RESIDENT_INFO,
    variables: {
      propertyId: homePropertyId,
    },
  },
  result: {
    data: {
      home: {
        invitedResidents: [],
        residents: [getTenant({ isAdmin: true })],
      },
    },
  },
};

export const onlyTenantMocks = [onlyTenantRequestMock];

export const onlyAdminMocks = [onlyAdminRequestMock];

export const deletedTenant = {
  deletedDatetime: "2020-06-20T20:12:00",
  emailAddress: null,
  familyName: null,
  givenName: null,
  id: "0170eef7b6a400018f32b810a6e47abc",
  isAdmin: false,
  joinedPropertyDatetime: null,
  remotelockCardNumber: null,
};

export const deletedTenantMocks = [
  {
    request: {
      query: GET_RESIDENT_INFO,
      variables: {
        propertyId: homePropertyId,
      },
    },
    result: {
      data: {
        home: {
          invitedResidents: [],
          residents: [deletedTenant],
        },
      },
    },
  },
];

export const entrataIntegrationNameLink = {
  landingURL: "",
  name: "Entrata",
};

export const resetMutationMocks = [
  {
    request: {
      query: RESET_INSTALLATION,
      variables: {
        propertyId,
      },
    },
    result: {
      data: {
        home: {
          id: "1234",
        },
      },
    },
  },
];

export const updateCardCredentialMutationMock = ({
  emailAddress,
  cardNumber,
  errorId,
  smartCardId,
}: {
  emailAddress: string;
  cardNumber: string;
  errorId: number;
  smartCardId: string;
}): any => [
  {
    error: { result: { error_id: errorId } },
    request: {
      query: UPDATE_CARD_CREDENTIALS,
      variables: {
        cardNumber,
        emailAddress,
        propertyId,
        smartCardId,
      },
    },
  },
];

export const getCommonAreaAccessDevicesMocks = [
  {
    request: {
      query: GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME,
      variables: {
        propertyId,
      },
    },
    result: {
      data: {
        home: {
          id: "1234",
          remotelockDoorGroups: [
            {
              device: [
                {
                  id: "1",
                },
              ],
              id: "12",
            },
          ],
          remotelockUnitAccessDevices: [],
        },
      },
    },
  },
];

export const getCommonAreaAccessDevicesWithNoDevicesMocks = [
  {
    request: {
      query: GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME,
      variables: {
        propertyId,
      },
    },
    result: {
      data: {
        home: {
          id: "1234",
          remotelockDoorGroups: [],
          remotelockUnitAccessDevices: [],
        },
      },
    },
  },
];

export const getUnitAccessDevicesMocks = [
  {
    request: {
      query: GET_DOOR_GROUPS_AND_UNIT_ACCESS_DEVICES_FOR_HOME,
      variables: {
        propertyId,
      },
    },
    result: {
      data: {
        home: {
          id: "1234",
          remotelockDoorGroups: [],
          remotelockUnitAccessDevices: [
            {
              device: {
                accessibleType: SMART_CARD_LOCK_TYPE,
                id: "589",
              },
              id: "12",
            },
          ],
        },
      },
    },
  },
];

export const GetResidentCardInfoMock = ({
  id = "ResidentInfoMock",
  remotelockCardNumber = "",
  smartCardCredentials = { badgeNumber: "12346", id: "12346" },
  userId = MOCK_USER_ID,
}: {
  id?: string;
  propertyId?: string;
  remotelockCardNumber?: string;
  userId?: string;
  smartCardCredentials?: ISmartCardCredentialsData;
}): MockedResponse => {
  return {
    request: {
      query: GET_RESIDENT_CARD_INFO,
      variables: {
        propertyId,
        userId,
      },
    },
    result: {
      data: {
        resident: {
          id,
          remotelockCardNumber,
          smartCardCredentials,
        },
      },
    },
  };
};

export const GetInvitedResidentCardInfoMock = ({
  emailAddress = MOCK_EMAIL_ADDRESS,
  id = "InvitedResidentInfoMock",
  remotelockCardNumber = "",
  smartCardCredentials = { badgeNumber: "12346", id: "12346" },
}: {
  emailAddress?: string;
  id?: string;
  propertyId?: string;
  remotelockCardNumber?: string;
  smartCardCredentials?: ISmartCardCredentialsData;
}): MockedResponse => {
  return {
    request: {
      query: GET_INVITED_RESIDENT_CARD_INFO,
      variables: {
        emailAddress,
        propertyId,
      },
    },
    result: {
      data: {
        invitedResident: {
          id,
          remotelockCardNumber,
          smartCardCredentials,
        },
      },
    },
  };
};
